import ContentPanel from "components/contentPanel/ContentPanel";
import moment from "moment";
import { memo, useMemo } from "react";
import styled from "styled-components";

const PreviewContent = styled.div`
  background-color: #f8f9fb;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const PreviewCaption = styled.div`
  padding: 15px 25px;
  font-size: 0.9rem;
  color: #666;
  border-top: solid 1px #e8e8ef;
`;

interface WebsitePreviewProps {
  domain: string;
  snapshotFiles: string[];
}

const WebsitePreview = (props: WebsitePreviewProps) => {
  const { domain, snapshotFiles } = props;

  // Find the latest snapshot file by parsing the date from filename (yyyyMMdd_hhmm)
  const latestSnapshot = useMemo(() => {
    if (!snapshotFiles || snapshotFiles.length === 0) return null;

    // Sort files by date (newest first)
    const sorted = [...snapshotFiles].sort((a, b) => {
      // Extract date parts from filename format: 20251102_2342
      const dateA = a.split("_")[0] + a.split("_")[1];
      const dateB = b.split("_")[0] + b.split("_")[1];
      return dateB.localeCompare(dateA);
    });

    return sorted[0];
  }, [snapshotFiles]);

  // Format the snapshot date for display (date only)
  const formattedDate = useMemo(() => {
    if (!latestSnapshot) return null;

    try {
      // Parse filename format: 20251102_2342 (yyyyMMdd_hhmm)
      const parts = latestSnapshot.split("_");
      const datePart = parts[0]; // 20251102

      const year = datePart.substring(0, 4);
      const month = datePart.substring(4, 6);
      const day = datePart.substring(6, 8);

      const date = moment(`${year}-${month}-${day}`, "YYYY-MM-DD");

      return date.format("DD MMMM YYYY");
    } catch {
      return null;
    }
  }, [latestSnapshot]);

  if (!latestSnapshot) return null;

  const imageUrl = `${process.env["REACT_APP_API_BASE_URL"]}/domains/preview/${encodeURIComponent(domain)}/${encodeURIComponent(
    latestSnapshot,
  )}`;

  return (
    <>
      <ContentPanel title="Latest Website Preview">
        <PreviewContent>
          <PreviewImage src={imageUrl} alt={`Website preview of ${domain}`} />
          {formattedDate && (
            <PreviewCaption>
              Snapshot captured on {formattedDate}
            </PreviewCaption>
          )}
        </PreviewContent>
      </ContentPanel>
    </>
  );
};

export default memo(WebsitePreview);
